import { EActivityType } from '@constants';
import { IProblemReportDownload } from '@sdk/contracts';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/Icon';
import { useDownloadProblemReports } from '@/store';
import { getDateTimeForFilename } from '@/utils';

export const DownloadProblemReports = () => {
  const { t } = useTranslation(['DefaultTranslations', 'ProblemReportsTranslations']);
  const { downloadProblemReports, isLoading } = useDownloadProblemReports();

  const getActivityTypeLabel = (type: EActivityType): string => {
    return t(`ProblemReportsTranslations:${type}`);
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  const downloadCSV = async () => {
    try {
      const data = await downloadProblemReports();

      if (!data || data.length === 0) return;

      const formattedData = data.map((item: IProblemReportDownload) => ({
        [t('ProblemReportsTranslations:TYPE')]: getActivityTypeLabel(item.type),
        [t('ProblemReportsTranslations:TITLE')]: item.resourceTitle,
        [t(`ProblemReportsTranslations:NOT_REVIEWED`)]: item.totalNotReviewedReports,
        [t(`ProblemReportsTranslations:IN_REVIEW`)]: item.totalInReviewReports,
        [t(`ProblemReportsTranslations:REVIEWED`)]: item.totalReviewedReports,
        [t('ProblemReportsTranslations:TOTAL_REPORTS')]: item.totalReports,
        [t('ProblemReportsTranslations:CREATION_DATE')]: formatDate(item.firstReportDate)
      }));

      const headers = Object.keys(formattedData[0]);
      const csvRows = [
        headers.join(','),
        ...formattedData.map(row =>
          headers
            .map(header => {
              const value = row[header as keyof typeof row];
              return `"${String(value).replace(/"/g, '""')}"`;
            })
            .join(',')
        )
      ];
      const csvString = csvRows.join('\n');

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `ProblemReports_${getDateTimeForFilename()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  return (
    <Button onClick={downloadCSV} loading={isLoading} icon={<Icon i="Download" />}>
      {t('ProblemReportsTranslations:DOWNLOAD_CSV')}
    </Button>
  );
};
